import Config from './../constants/Config';

export const AUTH_REFRESH_TOKEN_STORAGE_KEY = 'mymedhub.admin.v2.refreshToken';

export class AuthSessionError extends Error {
    constructor(message, status) {
        super(message);
        this.name = 'AuthSessionError';
        this.status = status;
    }
}

export class StaleAuthSessionError extends Error {
    constructor() {
        super('Результат относится к завершённой сессии');
        this.name = 'StaleAuthSessionError';
    }
}

export class AuthSession {
    constructor(options = {}) {
        this.apiDomain = options.apiDomain || Config.apiDomain;
        this.fetchImplementation = options.fetchImplementation || ((input, init) => fetch(input, init));
        this.storage = this.resolveStorage(options);
        this.accessToken = null;
        this.refreshToken = this.readRefreshToken();
        this.generation = 0;
        this.refreshOperation = null;
        this.invalidatedHandler = null;
        this.removeLegacyAccessToken();
    }

    setInvalidatedHandler(handler) {
        this.invalidatedHandler = handler;
    }

    getAccessToken() {
        return this.accessToken;
    }

    getRefreshToken() {
        return this.refreshToken;
    }

    isAuthenticated() {
        return typeof this.accessToken === 'string' && this.accessToken.length > 0;
    }

    login(login, password) {
        return this.performLogin(login, password);
    }

    async performLogin(login, password) {
        const generation = this.generation + 1;
        this.generation = generation;
        this.accessToken = null;
        this.replaceRefreshToken(null);
        this.refreshOperation = null;

        try {
            const response = await this.fetchImplementation(
                this.apiDomain + '/v2/auth/password/login',
                {
                    method: 'POST',
                    mode: 'cors',
                    headers: {
                        'Accept': 'application/json',
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({login, password}),
                },
            );
            const payload = await this.readTokenResponse(response, 'Не удалось войти');
            this.assertGeneration(generation);
            this.applyTokenPair(payload);

            return payload;
        } catch (error) {
            this.assertGeneration(generation);
            throw error;
        }
    }

    restore() {
        return this.refresh(this.generation);
    }

    refresh(expectedGeneration = this.generation) {
        this.assertGeneration(expectedGeneration);

        if (this.refreshOperation && this.refreshOperation.generation === expectedGeneration) {
            return this.refreshOperation.promise;
        }

        const presentedRefreshToken = this.refreshToken;
        if (!presentedRefreshToken) {
            this.invalidate(expectedGeneration);
            return Promise.reject(new AuthSessionError('Сессия не найдена', 401));
        }

        const operation = {
            generation: expectedGeneration,
            promise: null,
        };
        operation.promise = this.performRefresh(expectedGeneration, presentedRefreshToken)
            .finally(() => {
                if (this.refreshOperation === operation) {
                    this.refreshOperation = null;
                }
            });
        this.refreshOperation = operation;

        return operation.promise;
    }

    async performRefresh(expectedGeneration, presentedRefreshToken) {
        try {
            const response = await this.fetchImplementation(
                this.apiDomain + '/v2/auth/refresh',
                {
                    method: 'POST',
                    mode: 'cors',
                    headers: {
                        'Accept': 'application/json',
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({refresh_token: presentedRefreshToken}),
                },
            );
            const payload = await this.readTokenResponse(response, 'Сессия истекла');
            this.assertGeneration(expectedGeneration);
            this.applyTokenPair(payload);

            return payload;
        } catch (error) {
            this.assertGeneration(expectedGeneration);
            if (error instanceof AuthSessionError && error.status === 401) {
                this.invalidate(expectedGeneration);
            }
            throw error;
        }
    }

    async authenticatedFetch(input, init = {}) {
        const generation = this.generation;

        if (!this.isAuthenticated()) {
            await this.refresh(generation);
        }

        this.assertGeneration(generation);
        const tokenUsed = this.accessToken;
        let response;
        try {
            response = await this.fetchWithToken(input, init, tokenUsed);
        } catch (error) {
            this.assertGeneration(generation);
            throw error;
        }
        this.assertGeneration(generation);

        if (response.status !== 401) {
            return this.guardResponse(response, generation);
        }

        if (this.accessToken === tokenUsed) {
            await this.refresh(generation);
        }

        this.assertGeneration(generation);
        try {
            response = await this.fetchWithToken(input, init, this.accessToken);
        } catch (error) {
            this.assertGeneration(generation);
            throw error;
        }
        this.assertGeneration(generation);

        if (response.status === 401) {
            this.invalidate(generation);
            throw new AuthSessionError('Сессия истекла', 401);
        }

        return this.guardResponse(response, generation);
    }

    logout() {
        const accessToken = this.accessToken;
        const refreshToken = this.refreshToken;
        this.invalidate(this.generation);

        this.performRemoteLogout(accessToken, refreshToken).catch(() => null);

        return Promise.resolve(null);
    }

    performRemoteLogout(accessToken, refreshToken) {
        if (!accessToken) {
            return refreshToken
                ? this.refreshAndLogout(refreshToken)
                : Promise.resolve(null);
        }

        return this.sendLogout(accessToken)
            .then(response => {
                if (response.status !== 401 || !refreshToken) {
                    return response;
                }

                return this.refreshAndLogout(refreshToken)
                    .then(refreshedResponse => refreshedResponse || response);
            })
            .catch(() => null);
    }

    refreshAndLogout(refreshToken) {
        return this.fetchImplementation(
            this.apiDomain + '/v2/auth/refresh',
            {
                method: 'POST',
                mode: 'cors',
                headers: {
                    'Accept': 'application/json',
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({refresh_token: refreshToken}),
            },
        )
            .then(response => this.readTokenResponse(response, 'Сессия истекла'))
            .then(payload => this.sendLogout(payload.access_token))
            .catch(() => null);
    }

    sendLogout(accessToken) {
        return this.fetchImplementation(
            this.apiDomain + '/v2/auth/logout',
            {
                method: 'POST',
                mode: 'cors',
                headers: {
                    'Accept': 'application/json',
                    'Content-Type': 'application/json',
                    'Authorization': 'Bearer ' + accessToken,
                },
                body: JSON.stringify({}),
            },
        );
    }

    clear() {
        this.invalidate(this.generation);
    }

    async readTokenResponse(response, fallbackMessage) {
        let body = null;

        try {
            body = await response.json();
        } catch (error) {
            body = null;
        }

        if (!response.ok) {
            const message = body && body.message
                ? (Array.isArray(body.message) ? body.message.join(', ') : String(body.message))
                : fallbackMessage;
            throw new AuthSessionError(message, response.status);
        }

        if (
            !body ||
            typeof body.access_token !== 'string' ||
            body.access_token.length === 0 ||
            typeof body.refresh_token !== 'string' ||
            body.refresh_token.length === 0
        ) {
            throw new AuthSessionError(fallbackMessage, response.status);
        }

        return body;
    }

    fetchWithToken(input, init, accessToken) {
        return this.fetchImplementation(input, {
            ...init,
            headers: {
                ...(init.headers || {}),
                'Authorization': 'Bearer ' + accessToken,
            },
        });
    }

    guardResponse(response, generation) {
        ['json', 'text', 'blob', 'arrayBuffer', 'formData'].forEach(methodName => {
            if (typeof response[methodName] !== 'function') {
                return;
            }

            const originalMethod = response[methodName].bind(response);
            response[methodName] = (...args) => Promise.resolve(originalMethod(...args))
                .then(value => {
                    this.assertGeneration(generation);
                    return value;
                }, error => {
                    this.assertGeneration(generation);
                    throw error;
                });
        });

        return response;
    }

    applyTokenPair(payload) {
        this.accessToken = payload.access_token;
        this.replaceRefreshToken(payload.refresh_token);
    }

    invalidate(expectedGeneration) {
        if (this.generation !== expectedGeneration) {
            return;
        }

        this.generation += 1;
        this.accessToken = null;
        this.replaceRefreshToken(null);
        this.refreshOperation = null;

        if (this.invalidatedHandler) {
            this.invalidatedHandler();
        }
    }

    replaceRefreshToken(refreshToken) {
        this.refreshToken = refreshToken;

        if (!this.storage) {
            return;
        }

        try {
            if (refreshToken) {
                this.storage.setItem(AUTH_REFRESH_TOKEN_STORAGE_KEY, refreshToken);
            } else {
                this.storage.removeItem(AUTH_REFRESH_TOKEN_STORAGE_KEY);
            }
        } catch (error) {
            this.storage = null;
        }
    }

    readRefreshToken() {
        if (!this.storage) {
            return null;
        }

        try {
            return this.storage.getItem(AUTH_REFRESH_TOKEN_STORAGE_KEY);
        } catch (error) {
            this.storage = null;
            return null;
        }
    }

    resolveStorage(options) {
        if (Object.prototype.hasOwnProperty.call(options, 'storage')) {
            return options.storage;
        }

        try {
            return typeof window === 'undefined' ? null : window.sessionStorage;
        } catch (error) {
            return null;
        }
    }

    removeLegacyAccessToken() {
        try {
            if (typeof window !== 'undefined') {
                window.localStorage.removeItem('authToken');
            }
        } catch (error) {
            // Storage can be unavailable in hardened browser contexts.
        }
    }

    assertGeneration(expectedGeneration) {
        if (this.generation !== expectedGeneration) {
            throw new StaleAuthSessionError();
        }
    }
}

export const authSession = new AuthSession();

export function authenticatedFetch(input, init) {
    return authSession.authenticatedFetch(input, init);
}

export function unauthenticatedFetch(input, init) {
    return fetch(input, init);
}
